import './index.scss';

const OPEN_WIDTH_MAX = 669;
const OPEN_WIDTH_MAX_MOBILE = 576;
const OPEN_WIDTH_GUTTER = 16;
const OPEN_WIDTH_GUTTER_MOBILE = 24;
const MOBILE_BREAKPOINT = 768;

class Hotline {
    constructor(root) {
        this.root = root;
        this.shell = root.querySelector('[data-hotline-shell]');
        this.toggle = root.querySelector('[data-hotline-toggle]');
        this.content = root.querySelector('[data-hotline-content]');
        this.resizeTimer = null;

        if (!this.shell || !this.toggle || !this.content) {
            return;
        }

        this.onToggleClick = this.onToggleClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        this.toggle.addEventListener('click', this.onToggleClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('resize', this.onResize);

        this.setContentInteractive(false);
        this.measureOpenHeight();
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.measureOpenHeight());
        }
    }

    isOpen() {
        return this.root.classList.contains('is-open');
    }

    getOpenWidth() {
        const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
        const max = isMobile ? OPEN_WIDTH_MAX_MOBILE : OPEN_WIDTH_MAX;
        const gutter = isMobile ? OPEN_WIDTH_GUTTER_MOBILE : OPEN_WIDTH_GUTTER;
        return Math.min(max, window.innerWidth - gutter);
    }

    /**
     * Keep closed panel content out of the tab order while aria-hidden.
     */
    setContentInteractive(open) {
        if ('inert' in HTMLElement.prototype) {
            this.content.inert = !open;
            return;
        }

        this.content.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
            if (open) {
                if (node.dataset.hotlineTabindex != null) {
                    node.setAttribute('tabindex', node.dataset.hotlineTabindex);
                    delete node.dataset.hotlineTabindex;
                } else {
                    node.removeAttribute('tabindex');
                }
            } else {
                if (node.hasAttribute('tabindex')) {
                    node.dataset.hotlineTabindex = node.getAttribute('tabindex');
                }
                node.setAttribute('tabindex', '-1');
            }
        });
    }

    /**
     * Measure content at open width and set --hotline-open-height (px)
     * so the panel can animate height while still fitting the text.
     */
    measureOpenHeight() {
        const openWidth = this.getOpenWidth();
        const cloneRoot = this.root.cloneNode(true);
        cloneRoot.classList.add('is-open');
        cloneRoot.setAttribute('aria-hidden', 'true');
        cloneRoot.style.cssText = [
            'position: absolute',
            'left: -9999px',
            'top: 0',
            'visibility: hidden',
            'pointer-events: none',
        ].join(';');

        const cloneShell = cloneRoot.querySelector('[data-hotline-shell]');
        if (!cloneShell) {
            return;
        }

        cloneShell.style.cssText = [
            `width: ${openWidth}px`,
            'height: auto',
            'overflow: visible',
            'transition: none',
        ].join(';');

        const cloneContent = cloneRoot.querySelector('[data-hotline-content]');
        if (cloneContent) {
            cloneContent.style.width = `${openWidth}px`;
            cloneContent.style.opacity = '1';
        }

        document.body.appendChild(cloneRoot);
        const height = Math.ceil(cloneShell.getBoundingClientRect().height);
        cloneRoot.remove();

        if (height > 0) {
            this.root.style.setProperty('--hotline-open-height', `${height}px`);
        }
    }

    setOpen(open) {
        const wasOpen = this.isOpen();
        if (open) {
            this.measureOpenHeight();
        }
        this.root.classList.toggle('is-open', open);
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.content.setAttribute('aria-hidden', open ? 'false' : 'true');
        this.setContentInteractive(open);

        if (open && !wasOpen) {
            window.addEventListener('scroll', this.onScroll, { passive: true });
        } else if (!open && wasOpen) {
            window.removeEventListener('scroll', this.onScroll);
        }
    }

    onToggleClick(event) {
        event.stopPropagation();
        if (!this.isOpen()) {
            this.setOpen(true);
        }
    }

    onDocumentClick(event) {
        if (this.isOpen() && !this.root.contains(event.target)) {
            this.setOpen(false);
        }
    }

    onKeyDown(event) {
        if (event.key === 'Escape' && this.isOpen()) {
            this.setOpen(false);
            this.toggle.focus();
        }
    }

    onScroll() {
        if (this.isOpen()) {
            this.setOpen(false);
        }
    }

    onResize() {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.measureOpenHeight(), 100);
    }
}

window.app.run(() => {
    const floaters = Array.from(document.querySelectorAll('[data-hotline]'));
    if (!floaters.length) {
        return;
    }

    // Only one floater may be active; hide extras (editor misuse).
    floaters.slice(1).forEach((node) => {
        node.hidden = true;
        node.setAttribute('aria-hidden', 'true');
    });

    new Hotline(floaters[0]);
});
